import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { MapPin, ExternalLink } from 'lucide-react';
import { Button } from './button';

interface MapViewProps {
  latitude?: number;
  longitude?: number;
  address?: string;
  className?: string;
  height?: string;
  showControls?: boolean;
  interactive?: boolean;
}

export function MapView({ 
  latitude, 
  longitude, 
  address, 
  className,
  height = "300px",
  showControls = true,
  interactive = true
}: MapViewProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [useStatic, setUseStatic] = useState(false);

  // Récupérer le token Mapbox depuis les variables d'environnement
  const MAPBOX_TOKEN = import.meta.env?.VITE_MAPBOX_TOKEN;
  
  // Debug: log du token pour vérifier qu'il est présent
  console.log('🗺️ MapView Debug:', {
    hasToken: !!MAPBOX_TOKEN,
    tokenPrefix: MAPBOX_TOKEN ? MAPBOX_TOKEN.substring(0, 10) + '...' : 'NONE',
    latitude,
    longitude,
    address
  });

  useEffect(() => {
    if (!latitude || !longitude || !MAPBOX_TOKEN || !mapContainer.current) {
      setIsLoading(false);
      return;
    }

    const initializeMap = async () => {
      try {
        console.log('🗺️ Initializing Mapbox with token:', MAPBOX_TOKEN.substring(0, 10) + '...');
        
        // Import dynamique de Mapbox GL avec CSS
        const [mapboxgl] = await Promise.all([
          import('mapbox-gl'),
          import('mapbox-gl/dist/mapbox-gl.css')
        ]);

        console.log('✅ Mapbox GL modules loaded successfully');

        const MapboxGL = mapboxgl.default;
        MapboxGL.accessToken = MAPBOX_TOKEN;

        // Vérifier le support
        if (!MapboxGL.supported()) {
          console.warn('⚠️ Mapbox GL not supported, using static map');
          setUseStatic(true);
          setIsLoading(false);
          return;
        }

        console.log('✅ Mapbox GL is supported');

        // Nettoyer l'ancienne carte
        if (map.current) {
          try {
            map.current.remove();
          } catch (e) {
            console.warn('Error removing old map:', e);
          }
          map.current = null;
        }

        // Créer la nouvelle carte
        map.current = new MapboxGL.Map({
          container: mapContainer.current!,
          style: 'mapbox://styles/mapbox/light-v11',
          center: [longitude, latitude],
          zoom: 15,
          interactive,
          attributionControl: false
        });

        console.log('✅ Mapbox map created successfully', map.current);

        // Ajouter les contrôles
        if (showControls && interactive) {
          map.current.addControl(new MapboxGL.NavigationControl(), 'top-right');
        }

        // Ajouter le marker
        new MapboxGL.Marker({
          color: 'hsl(var(--primary))'
        })
        .setLngLat([longitude, latitude])
        .addTo(map.current);

        // Ajouter le popup si adresse
        if (address) {
          new MapboxGL.Popup({
            offset: 25,
            closeButton: false,
            closeOnClick: false
          })
          .setLngLat([longitude, latitude])
          .setHTML(`<div class="text-sm font-medium">${address}</div>`)
          .addTo(map.current);
        }

        // Gestion simple du chargement
        map.current.on('load', () => {
          setIsLoading(false);
          // Redimensionner après chargement
          setTimeout(() => {
            if (map.current) {
              map.current.resize();
            }
          }, 100);
        });

        // Gestion des erreurs
        map.current.on('error', (e: any) => {
          console.error('Mapbox error:', e);
          setUseStatic(true);
          setIsLoading(false);
        });

        // Timeout de sécurité plus long
        setTimeout(() => {
          if (isLoading) {
            console.warn('Map loading timeout');
            setUseStatic(true);
            setIsLoading(false);
          }
        }, 10000);

      } catch (error) {
        console.error('Failed to initialize map:', error);
        setUseStatic(true);
        setIsLoading(false);
      }
    };

    initializeMap();

    return () => {
      if (map.current) {
        try {
          map.current.remove();
        } catch (e) {
          console.warn('Error cleaning up map:', e);
        }
        map.current = null;
      }
    };
  }, [latitude, longitude, MAPBOX_TOKEN, address, showControls, interactive]);

  const openInMaps = () => {
    if (latitude && longitude) {
      const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
      window.open(url, '_blank');
    }
  };

   // Si pas de coordonnées ou pas de token, afficher un fallback
  if (!latitude || !longitude) {
    return address ? (
      <div className={cn(
        "border border-border rounded-lg p-6 bg-muted/30",
        className
      )} style={{ height }}>
        <div className="flex items-center justify-center h-full">
          <div className="text-center space-y-3">
            <MapPin className="h-8 w-8 text-muted-foreground mx-auto" />
            <div>
              <p className="font-medium text-sm">{address}</p>
              <p className="text-xs text-muted-foreground mt-1">
                Localisation non géolocalisée
              </p>
            </div>
          </div>
        </div>
      </div>
    ) : null;
  }

  // Si pas de token Mapbox, afficher un message spécifique
  if (!MAPBOX_TOKEN) {
    console.error('❌ MAPBOX_TOKEN manquant dans les variables d\'environnement');
    return (
      <div className={cn(
        "border border-border rounded-lg p-6 bg-muted/30",
        className
      )} style={{ height }}>
        <div className="flex items-center justify-center h-full">
          <div className="text-center space-y-3">
            <MapPin className="h-8 w-8 text-muted-foreground mx-auto" />
            <div>
              {address && <p className="font-medium text-sm">{address}</p>}
              <p className="text-xs text-muted-foreground mt-1">
                Token Mapbox manquant - Vérifiez VITE_MAPBOX_TOKEN
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Fallback vers carte statique si Mapbox ne fonctionne pas
  if (useStatic && latitude && longitude) {
    return (
      <div className={cn("relative rounded-lg overflow-hidden border border-border", className)}>
        <div className="w-full bg-muted/30 flex items-center justify-center" style={{ height }}>
          <div className="text-center space-y-3">
            <MapPin className="h-8 w-8 text-muted-foreground mx-auto" />
            <div>
              {address && <p className="font-medium text-sm">{address}</p>}
              <p className="text-xs text-muted-foreground mt-1">
                Carte non disponible
              </p>
            </div>
          </div>
        </div>
        
        {/* Bouton pour ouvrir dans Google Maps */}
        <div className="absolute top-2 right-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={openInMaps}
            className="h-8 w-8 p-0 bg-background/90 backdrop-blur-sm"
          >
            <ExternalLink className="h-3 w-3" />
          </Button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn(
        "border border-border rounded-lg p-6 bg-muted/30 flex items-center justify-center",
        className
      )} style={{ height }}>
        <div className="text-center space-y-2">
          <MapPin className="h-8 w-8 text-muted-foreground mx-auto" />
          <p className="text-sm text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("relative rounded-lg overflow-hidden border border-border", className)}>
      <div 
        ref={mapContainer} 
        className="w-full"
        style={{ height }}
      />
      
      {isLoading && (
        <div className="absolute inset-0 bg-muted/80 flex items-center justify-center">
          <div className="text-center space-y-2">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="text-sm text-muted-foreground">Chargement de la carte...</p>
          </div>
        </div>
      )}

      {/* Bouton pour ouvrir dans Google Maps */}
      <div className="absolute top-2 right-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={openInMaps}
          className="h-8 w-8 p-0 bg-background/90 backdrop-blur-sm"
        >
          <ExternalLink className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}